import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Plus, Trash2, X, Upload, Loader2 } from "lucide-react";
import api, { imgUrl } from "@/lib/api";

export default function LogosAdmin() {
  const [logos, setLogos] = useState([]);
  const [modal, setModal] = useState(false);
  const [name, setName] = useState("");
  const [seo, setSeo] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const load = () => api.get("/logos").then((r) => setLogos(r.data));
  useEffect(() => { load(); }, []);

  const onFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f); setPreview(URL.createObjectURL(f));
    if (!seo) setSeo(f.name.replace(/\.[^.]+$/, ""));
  };

  const save = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Ajoutez un logo.");
    if (!name.trim()) return toast.error("Nom du client requis.");
    setSaving(true);
    const fd = new FormData();
    fd.append("name", name); fd.append("seo_name", seo); fd.append("file", file);
    try {
      await api.post("/admin/logos", fd);
      toast.success("Logo ajouté");
      setModal(false); setName(""); setSeo(""); setFile(null); setPreview(null); load();
    } catch { toast.error("Erreur"); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce logo ?")) return;
    await api.delete(`/admin/logos/${id}`); load();
  };

  const [selected, setSelected] = useState(new Set());
  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const clearSel = () => setSelected(new Set());
  const bulkDelete = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    if (!window.confirm(`Supprimer ${ids.length} logo(s) ?`)) return;
    try {
      await api.post("/admin/logos/bulk-delete", { ids });
      toast.success(`${ids.length} logo(s) supprimé(s)`);
      clearSel(); load();
    } catch { toast.error("Erreur lors de la suppression"); }
  };

  const [bulkBusy, setBulkBusy] = useState(false);
  const bulkRef = useRef();

  const prettyName = (filename) =>
    filename.replace(/\.[^.]+$/, "").replace(/[-_]+/g, " ").replace(/\s+/g, " ").trim();

  const onBulk = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBulkBusy(true);
    let ok = 0, fail = 0;
    for (const f of files) {
      const fd = new FormData();
      fd.append("name", prettyName(f.name) || "Client");
      fd.append("seo_name", f.name.replace(/\.[^.]+$/, ""));
      fd.append("file", f);
      try { await api.post("/admin/logos", fd); ok++; } catch { fail++; }
    }
    setBulkBusy(false);
    if (bulkRef.current) bulkRef.current.value = "";
    toast.success(`${ok} logo(s) importé(s)${fail ? `, ${fail} échec(s)` : ""}`);
    load();
  };

  const inputCls = "w-full bg-zinc-900 border border-white/10 rounded-md px-3 py-2.5 text-sm text-white placeholder-zinc-500 focus:border-yellow-400 focus:outline-none";

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight">Logos clients</h1>
          <p className="text-zinc-500 text-sm mt-1">{logos.length} logos</p>
        </div>
        <div className="flex items-center gap-2">
          <input ref={bulkRef} data-testid="logo-bulk-input" type="file" accept="image/*" multiple onChange={onBulk} className="hidden" />
          <button data-testid="logo-bulk" onClick={() => bulkRef.current?.click()} disabled={bulkBusy} className="flex items-center gap-2 bg-white/10 hover:bg-white/15 text-white font-semibold px-4 py-2.5 rounded-md disabled:opacity-60">
            {bulkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Import multiple
          </button>
          <button data-testid="logo-add" onClick={() => setModal(true)} className="flex items-center gap-2 bg-yellow-400 text-black font-bold px-4 py-2.5 rounded-md hover:bg-yellow-300">
            <Plus className="w-4 h-4" /> Ajouter
          </button>
        </div>
      </div>
      <p className="text-xs text-zinc-500 mb-4">💡 Astuce : téléchargez votre dossier Google Drive sur votre ordinateur, puis cliquez sur « Import multiple » et sélectionnez tous les logos d'un coup. Le nom du client est déduit du nom de fichier (modifiable ensuite).</p>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <label className="flex items-center gap-2 text-sm text-zinc-400 cursor-pointer">
          <input
            data-testid="logo-select-all"
            type="checkbox"
            checked={logos.length > 0 && logos.every((l) => selected.has(l.id))}
            onChange={(e) => setSelected(e.target.checked ? new Set(logos.map((l) => l.id)) : new Set())}
            className="accent-yellow-400 w-4 h-4"
          />
          Tout sélectionner
        </label>
        {selected.size > 0 && (
          <div data-testid="logo-bulk-toolbar" className="flex items-center gap-2 bg-[#18181B] border border-white/10 rounded-lg px-3 py-2">
            <span className="text-sm text-yellow-400 font-semibold">{selected.size} sélectionné(s)</span>
            <button data-testid="logo-bulk-delete" onClick={bulkDelete} className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm bg-red-500/15 text-red-400 hover:bg-red-500 hover:text-white">
              <Trash2 className="w-3.5 h-3.5" /> Supprimer
            </button>
            <button data-testid="logo-bulk-clear" onClick={clearSel} className="px-2 py-1.5 rounded-md text-sm text-zinc-400 hover:text-white">Annuler</button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-4">
        {logos.map((l) => (
          <div key={l.id} data-testid="admin-logo-item" className={`group relative rounded-xl bg-white/5 border p-4 h-28 flex items-center justify-center ${selected.has(l.id) ? "border-yellow-400" : "border-white/5"}`}>
            <label className="absolute top-2 left-2 z-10 cursor-pointer">
              <input data-testid="logo-select" type="checkbox" checked={selected.has(l.id)} onChange={() => toggleSel(l.id)} className="accent-yellow-400 w-4 h-4 rounded" />
            </label>
            <img src={imgUrl(l.image_url)} alt={l.name} className="max-h-16 max-w-full object-contain" />
            <button data-testid="logo-delete" onClick={() => remove(l.id)} className="absolute top-2 right-2 p-1.5 rounded-md bg-black/60 opacity-0 group-hover:opacity-100 hover:bg-red-500 transition-opacity">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
            <span className="absolute bottom-1 left-0 right-0 text-center text-[10px] text-zinc-500 truncate px-1">{l.name}</span>
          </div>
        ))}
      </div>

      {modal && (
        <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setModal(false)}>
          <div className="bg-[#18181B] border border-white/10 rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display text-xl font-bold">Nouveau logo client</h2>
              <button onClick={() => setModal(false)}><X className="w-5 h-5 text-zinc-400" /></button>
            </div>
            <form onSubmit={save} className="space-y-4">
              <div onClick={() => fileRef.current?.click()} className="cursor-pointer border-2 border-dashed border-white/15 rounded-xl h-32 flex flex-col items-center justify-center bg-white/5 hover:border-yellow-400 transition-colors">
                {preview ? <img src={preview} alt="preview" className="h-full object-contain p-2" /> : <><Upload className="w-6 h-6 text-zinc-500 mb-2" /><span className="text-sm text-zinc-500">Choisir un logo</span></>}
              </div>
              <input ref={fileRef} data-testid="logo-file" type="file" accept="image/*" onChange={onFile} className="hidden" />
              <input data-testid="logo-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nom du client" className={inputCls} />
              <input data-testid="logo-seo" value={seo} onChange={(e) => setSeo(e.target.value)} placeholder="Nom SEO du fichier" className={inputCls} />
              <button data-testid="logo-save" disabled={saving} className="w-full flex items-center justify-center gap-2 bg-yellow-400 text-black font-bold py-3 rounded-md hover:bg-yellow-300 disabled:opacity-60">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />} Enregistrer
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
